import { czyNumerWypowiedziWZakresie } from "@shared/numeracja.js";
import type { DodajPominiete, PominieteWskazanie, ZapisPominietego } from "@shared/recenzjeTypy.js";

/**
 * Uwagi recenzenta przy wypowiedzi: czyste funkcje (zero DOM, zero sieci), zeby dalo sie je testowac wprost.
 *
 * Uwaga to ten sam rekord co "pominiete" w API (ExpertCorrection MISSED_FINDING), ale od zmiany widoku zawsze z numerem
 * wypowiedzi. Rekord z numerem null (uwaga do calej rozmowy) kontrakt nadal dopuszcza, wiec widok go obsluguje.
 *
 * Trzy warstwy:
 *  - lista uwag rozmowy i otwarte formularze (reduktor): jedyne wspolne zrodlo prawdy dla transkryptu i paska postepu,
 *    zmienia sie WYLACZNIE gdy wraca wynik zapisu albo usuniecia, nigdy przy pisaniu (tekst pola zyje w formularzu),
 *  - widok pod wypowiedzia (`zbudujWidokUwag`): tablice wpisow o stalej tozsamosci, wiec wiersz transkryptu przerysowuje
 *    sie tylko wtedy, gdy zmienilo sie cos jego,
 *  - szkic uwagi w magazynie (sessionStorage): to, czego serwer jeszcze nie przyjal.
 */

// ---------------------------------------------------------------------------
// Uwaga i szkic
// ---------------------------------------------------------------------------

/**
 * Uwaga na liscie przegladu: rekord z serwera plus `klucz`, czyli lokalna tozsamosc. Klucz jest kluczem Reacta i nazwa
 * pola kolejki zapisu. Dla uwagi z serwera to jej id, dla swiezej (jeszcze nie zapisanej) napis z `nowyKluczUwagi`; po
 * pierwszym zapisie klucz zostaje ten sam, wiec formularz nie traci stanu ani fokusu.
 */
export interface UwagaRecenzji extends PominieteWskazanie {
  klucz: string;
}

/** To, co kolejka zapisu ma wyslac dla jednej uwagi, i to, co trafia do magazynu szkicow. */
export interface SzkicUwagi {
  /** Numer wypowiedzi od 1; null: uwaga do calej rozmowy (tylko rekordy sprzed zmiany widoku). */
  numer: number | null;
  /** Tytul parametru albo null ("Nie wybieram"). */
  parametr: string | null;
  komentarz: string;
  /** Id uwagi na serwerze, gdy to pole juz ja zapisalo (wtedy zapis to PUT); null: pierwszy zapis to POST. */
  id: string | null;
  /** To pole ma USUNAC uwage (tylko po jawnym potwierdzeniu w oknie), a nie zapisac tresc. */
  usun: boolean;
}

/** Serwer wymaga tresci po przycieciu, wiec sam odstep to brak tresci: nic nie wysylamy. */
export function czyPustaTresc(komentarz: string): boolean {
  return komentarz.trim().length === 0;
}

const PREFIKS_NOWEJ = "nowa-";
let licznikKluczy = 0;

/** Klucz swiezej uwagi. Losowy ogon jest po to, ze po przeladowaniu licznik startuje od nowa, a szkic o starym kluczu wraca z magazynu. */
export function nowyKluczUwagi(losowe: () => number = Math.random): string {
  licznikKluczy += 1;
  return `${PREFIKS_NOWEJ}${licznikKluczy.toString(36)}${losowe().toString(36).slice(2, 8)}`;
}

export function uwagaZSerwera(uwaga: PominieteWskazanie, klucz: string = uwaga.id): UwagaRecenzji {
  return { ...uwaga, klucz };
}

/** Cialo POST: pierwszy zapis uwagi. */
export function cialoDodania(szkic: SzkicUwagi): DodajPominiete {
  return { numerWypowiedzi: szkic.numer, parametr: szkic.parametr, komentarz: szkic.komentarz };
}

/** Cialo PUT: zmiana tresci i parametru zapisanej uwagi (numer wypowiedzi sie nie zmienia). */
export function cialoZmiany(szkic: SzkicUwagi): ZapisPominietego {
  return { parametr: szkic.parametr, komentarz: szkic.komentarz };
}

/**
 * Szkic uwagi z magazynu albo null, gdy ksztalt sie nie zgadza. Magazyn to dane niezaufane (ktos mogl je zmienic w
 * DevTools), wiec sprawdzamy kazde pole.
 */
export function odczytajSzkicUwagi(surowy: unknown): SzkicUwagi | null {
  if (typeof surowy !== "object" || surowy === null) return null;
  const { numer, parametr, komentarz, id, usun } = surowy as Record<string, unknown>;
  if (numer !== null && !(typeof numer === "number" && Number.isInteger(numer) && numer >= 1)) return null;
  if (parametr !== null && typeof parametr !== "string") return null;
  if (typeof komentarz !== "string") return null;
  if (id !== null && typeof id !== "string") return null;
  if (typeof usun !== "boolean") return null;
  return { numer, parametr, komentarz, id, usun };
}

// ---------------------------------------------------------------------------
// Napisy
// ---------------------------------------------------------------------------

/** "wypowiedzi 12" albo "calej rozmowy": dopelniacz do zdan typu "Edytuj uwage do ...". */
function celDoZdania(numer: number | null): string {
  return numer === null ? "całej rozmowy" : `wypowiedzi ${numer}`;
}

/** Ile znakow tresci uwagi wchodzi do nazwy przycisku. */
const DLUGOSC_ZAJAWKI = 30;

/**
 * Poczatek tresci uwagi do nazw przyciskow: kilka uwag pod jedna wypowiedzia ma sie dac odroznic bez czytania calosci,
 * a "Usun" jest nieodwracalne. Biale znaki zlewaja sie w jedna spacje, dluzszy tekst konczy sie wielokropkiem.
 */
export function zajawkaUwagi(komentarz: string): string {
  const znaki = Array.from(komentarz.replace(/\s+/g, " ").trim());
  if (znaki.length <= DLUGOSC_ZAJAWKI) return znaki.join("");
  return `${znaki.slice(0, DLUGOSC_ZAJAWKI).join("").trimEnd()}...`;
}

function zZajawka(komentarz: string | undefined): string {
  const zajawka = komentarz === undefined ? "" : zajawkaUwagi(komentarz);
  return zajawka === "" ? "" : `: "${zajawka}"`;
}

/** Ktora to uwaga pod wypowiedzia, gdy jest ich wiecej niz jedna ("uwaga 2 z 3"). */
export interface PozycjaUwagi {
  nr: number;
  z: number;
}

export const tytulUwagi = (numer: number | null, pozycja: PozycjaUwagi | null = null): string =>
  `Twoja uwaga do ${celDoZdania(numer)}${pozycja ? ` (uwaga ${pozycja.nr} z ${pozycja.z})` : ""}`;
export const nazwaDodaniaUwagi = (numer: number): string => `Dodaj uwagę do wypowiedzi ${numer}`;
export const nazwaEdycjiUwagi = (numer: number | null, komentarz?: string): string =>
  `Edytuj uwagę do ${celDoZdania(numer)}${zZajawka(komentarz)}`;
export const nazwaUsunieciaUwagi = (numer: number | null, komentarz?: string): string =>
  `Usuń uwagę do ${celDoZdania(numer)}${zZajawka(komentarz)}`;

/** Tytul okna potwierdzenia: cel malymi literami, jak w pozostalych oknach usuwania ("Usunąć uwagę: wypowiedź 3?"). */
export function tytulUsunieciaUwagi(numer: number | null): string {
  return `Usunąć uwagę: ${numer === null ? "cała rozmowa" : `wypowiedź ${numer}`}?`;
}

// ---------------------------------------------------------------------------
// Lista uwag i otwarte formularze
// ---------------------------------------------------------------------------

export interface OtwartaUwaga {
  klucz: string;
  numer: number | null;
  /** Formularz ma przejac fokus po pojawieniu sie (otwarty przez recenzenta); przywrocony z magazynu go nie zabiera. */
  fokus: boolean;
  /**
   * Szkic przywrocony z magazynu (niezapisany w poprzedniej wizycie): jego tresc wygrywa z uwaga z serwera, ale TYLKO
   * w formularzu otwartym przy wejsciu na rozmowe. Po zamknieciu wpis znika razem z nim, wiec kolejne "Edytuj" bierze
   * biezaca tresc uwagi, a nie stary szkic.
   */
  szkic: SzkicUwagi | null;
}

export interface StanUwag {
  /** Uwagi zapisane na serwerze, w kolejnosci z serwera, nowe na koncu. */
  uwagi: readonly UwagaRecenzji[];
  /** Formularze otwarte w tej chwili: nowe (bez wpisu na liscie) i edytowane (z wpisem). */
  otwarte: readonly OtwartaUwaga[];
  /** Klucze uwag, ktorych usuniecie trwa (karta pokazuje "Usuwam..." i nie przyjmuje kolejnych klikniec). */
  usuwane: readonly string[];
}

export type AkcjaUwag =
  | { typ: "otworz-nowa"; klucz: string; numer: number | null }
  | { typ: "edytuj"; klucz: string }
  | { typ: "zamknij"; klucz: string }
  | { typ: "zapisano"; uwaga: UwagaRecenzji }
  | { typ: "usuwanie"; klucz: string; trwa: boolean }
  | { typ: "usunieto"; klucz: string };

export const PUSTY_STAN_UWAG: StanUwag = { uwagi: [], otwarte: [], usuwane: [] };

export function reduktorUwag(stan: StanUwag, akcja: AkcjaUwag): StanUwag {
  switch (akcja.typ) {
    case "otworz-nowa":
      if (stan.otwarte.some((o) => o.klucz === akcja.klucz)) return stan;
      return { ...stan, otwarte: [...stan.otwarte, { klucz: akcja.klucz, numer: akcja.numer, fokus: true, szkic: null }] };

    case "edytuj": {
      const uwaga = stan.uwagi.find((u) => u.klucz === akcja.klucz);
      if (!uwaga || stan.otwarte.some((o) => o.klucz === akcja.klucz)) return stan;
      return {
        ...stan,
        otwarte: [...stan.otwarte, { klucz: akcja.klucz, numer: uwaga.numerWypowiedzi, fokus: true, szkic: null }],
      };
    }

    case "zamknij":
      if (!stan.otwarte.some((o) => o.klucz === akcja.klucz)) return stan;
      return { ...stan, otwarte: stan.otwarte.filter((o) => o.klucz !== akcja.klucz) };

    case "zapisano": {
      const jest = stan.uwagi.some((u) => u.klucz === akcja.uwaga.klucz);
      return {
        ...stan,
        uwagi: jest
          ? stan.uwagi.map((u) => (u.klucz === akcja.uwaga.klucz ? akcja.uwaga : u))
          : [...stan.uwagi, akcja.uwaga],
      };
    }

    case "usuwanie": {
      const jest = stan.usuwane.includes(akcja.klucz);
      if (akcja.trwa === jest) return stan;
      return { ...stan, usuwane: akcja.trwa ? [...stan.usuwane, akcja.klucz] : stan.usuwane.filter((k) => k !== akcja.klucz) };
    }

    case "usunieto":
      return {
        uwagi: stan.uwagi.filter((u) => u.klucz !== akcja.klucz),
        otwarte: stan.otwarte.filter((o) => o.klucz !== akcja.klucz),
        usuwane: stan.usuwane.filter((k) => k !== akcja.klucz),
      };
  }
}

// ---------------------------------------------------------------------------
// Widok pod wypowiedzia
// ---------------------------------------------------------------------------

/** Jedna pozycja pod wypowiedzia: karta zapisanej uwagi albo formularz (nowy albo edycja zapisanej). */
export interface WpisUwagi {
  klucz: string;
  numer: number | null;
  /** Zapisana na serwerze albo null (nowa, jeszcze bez pierwszego zapisu). */
  uwaga: UwagaRecenzji | null;
  /** Pod tym kluczem stoi formularz (zamiast karty). */
  otwarta: boolean;
  fokus: boolean;
  /** Szkic z magazynu dla formularza otwartego przy wejsciu na rozmowe (patrz OtwartaUwaga.szkic). */
  szkic: SzkicUwagi | null;
  usuwana: boolean;
}

export type WidokUwag = ReadonlyMap<number | null, readonly WpisUwagi[]>;

/** Jedna, wspolna pusta tablica: wiersz bez uwag dostaje zawsze ta sama referencje, wiec `memo` go nie przerysowuje. */
export const BRAK_WPISOW: readonly WpisUwagi[] = [];
export const PUSTY_WIDOK: WidokUwag = new Map();

function tenSamWpis(a: WpisUwagi, b: WpisUwagi): boolean {
  return (
    a.klucz === b.klucz &&
    a.numer === b.numer &&
    a.uwaga === b.uwaga &&
    a.otwarta === b.otwarta &&
    a.fokus === b.fokus &&
    a.szkic === b.szkic &&
    a.usuwana === b.usuwana
  );
}

/**
 * Wpisy pogrupowane po numerze wypowiedzi: najpierw uwagi zapisane (w kolejnosci z serwera), potem formularze nowych
 * uwag. Tablice i wpisy, ktore sie nie zmienily wzgledem `poprzedni`, zostaja TE SAME referencje (wspoldzielenie
 * struktury): zapis jednej uwagi nie przerysowuje setek innych wierszy transkryptu.
 */
export function zbudujWidokUwag(stan: StanUwag, poprzedni: WidokUwag = PUSTY_WIDOK): WidokUwag {
  const otwarte = new Map(stan.otwarte.map((o) => [o.klucz, o] as const));
  const zapisane = new Set(stan.uwagi.map((u) => u.klucz));
  const swieze = new Map<number | null, WpisUwagi[]>();
  const dolacz = (wpis: WpisUwagi): void => {
    const lista = swieze.get(wpis.numer);
    if (lista) lista.push(wpis);
    else swieze.set(wpis.numer, [wpis]);
  };

  for (const uwaga of stan.uwagi) {
    const o = otwarte.get(uwaga.klucz);
    dolacz({
      klucz: uwaga.klucz,
      numer: uwaga.numerWypowiedzi,
      uwaga,
      otwarta: o !== undefined,
      fokus: o?.fokus ?? false,
      szkic: o?.szkic ?? null,
      usuwana: stan.usuwane.includes(uwaga.klucz),
    });
  }
  for (const o of stan.otwarte) {
    if (zapisane.has(o.klucz)) continue;
    dolacz({ klucz: o.klucz, numer: o.numer, uwaga: null, otwarta: true, fokus: o.fokus, szkic: o.szkic, usuwana: false });
  }

  const wynik = new Map<number | null, readonly WpisUwagi[]>();
  for (const [numer, lista] of swieze) {
    const stara = poprzedni.get(numer);
    const zWspolnymiWpisami = lista.map((wpis) => stara?.find((s) => tenSamWpis(s, wpis)) ?? wpis);
    const bezZmian =
      stara !== undefined && stara.length === zWspolnymiWpisami.length && stara.every((s, i) => s === zWspolnymiWpisami[i]);
    wynik.set(numer, bezZmian ? stara : zWspolnymiWpisami);
  }
  return wynik;
}

/**
 * Ile wypowiedzi transkrypt ma pokazac na starcie: `porcja`, a gdy JAKIKOLWIEK wpis (zapisana uwaga, szkic przywrocony
 * z magazynu albo otwarty formularz) stoi przy wypowiedzi dalszej niz ta porcja, tyle, zeby najdalszy z nich byl widoczny.
 * Inaczej uwaga zapisana przy wypowiedzi 70 nie byloby widac po wejsciu na rozmowe, choc pasek postepu ja liczy,
 * a niezapisana tresc wisialaby w pamieci bez komunikatu o bledzie zapisu. Wpisy bez wypowiedzi w transkrypcie
 * (numer null albo spoza rozmowy) nie rozszerzaja okna: maja swoja karte.
 */
export function ileWypowiedziNaStart(wypowiedzi: readonly { numer: number }[], widok: WidokUwag, porcja: number): number {
  let ile = porcja;
  wypowiedzi.forEach((w, i) => {
    if (i + 1 > ile && (widok.get(w.numer)?.length ?? 0) > 0) ile = i + 1;
  });
  return ile;
}

/** Ile ZAPISANYCH uwag stoi przy wypowiedziach od pozycji `od` (indeks w tablicy wypowiedzi) do konca: do napisu "Pokaz dalsze". */
export function ileUwagOdPozycji(wypowiedzi: readonly { numer: number }[], widok: WidokUwag, od: number): number {
  let suma = 0;
  for (let i = Math.max(0, od); i < wypowiedzi.length; i++) {
    suma += widok.get(wypowiedzi[i].numer)?.filter((wpis) => wpis.uwaga !== null).length ?? 0;
  }
  return suma;
}

/** Miejsce wpisu wsrod wpisow tej samej wypowiedzi ("uwaga 2 z 3") albo null, gdy to jedyny wpis przy tej wypowiedzi. */
export function pozycjaWpisu(wpisy: readonly WpisUwagi[], wpis: WpisUwagi): PozycjaUwagi | null {
  const razem = wpisy.filter((w) => w.numer === wpis.numer);
  if (razem.length < 2) return null;
  return { nr: razem.indexOf(wpis) + 1, z: razem.length };
}

/** Wpisy, ktore nie maja wypowiedzi w transkrypcie (numer null albo spoza rozmowy): widok pokazuje je osobno. */
export function wpisyBezWypowiedzi(widok: WidokUwag, liczbaWypowiedzi: number): WpisUwagi[] {
  const wynik: WpisUwagi[] = [];
  for (const [numer, lista] of widok) {
    if (numer === null || !czyNumerWypowiedziWZakresie(numer, liczbaWypowiedzi)) wynik.push(...lista);
  }
  return wynik;
}

// ---------------------------------------------------------------------------
// Stan poczatkowy: uwagi z serwera plus szkice z poprzedniej wizyty
// ---------------------------------------------------------------------------

export interface PoczatekUwag {
  stan: StanUwag;
  /** Szkice tresci do wyslania zaraz po wejsciu: serwer ich nie przyjal w poprzedniej wizycie. */
  doZapisu: { klucz: string; szkic: SzkicUwagi }[];
  /** Usuniecia, ktore nie doszly do skutku w poprzedniej wizycie. */
  doUsuniecia: { klucz: string; szkic: SzkicUwagi }[];
  /** Wpisy magazynu bez sensu (brak wypowiedzi, pusta tresc, usuwanie nieznanej uwagi): do skasowania. */
  doPorzucenia: string[];
  /** Klucz uwagi -> id na serwerze (null: jeszcze nie zapisana) dla wszystkich uwag, ktorymi zajmie sie kolejka. */
  ids: Map<string, string | null>;
}

/**
 * Sklada stan z uwag serwera i szkicow z magazynu.
 *
 * - Uwaga serwera, do ktorej wskazuje szkic (po `id`), dostaje KLUCZ SZKICU: jedno pole kolejki na uwage, takze po
 *   przeladowaniu (inaczej dwa pola pisalyby do jednej uwagi i traciliby kolejnosc).
 * - Szkic z trescia otwiera formularz (bez zabierania fokusu) i leci na serwer; szkic wskazujacy nieznane id to nowa uwaga
 *   (tresc nie moze zginac), a szkic z pusta trescia i usuniecia nieznanej uwagi ida do kosza. Szkic z pusta trescia
 *   NIGDY nie usuwa uwagi z serwera, nawet gdy wskazuje jej id: usuwa tylko szkic z `usun`, a ten powstaje wylacznie po
 *   potwierdzeniu usuniecia w oknie.
 */
export function poczatekUwag(
  serwerowe: readonly PominieteWskazanie[],
  szkice: ReadonlyMap<string, SzkicUwagi>,
  liczbaWypowiedzi: number,
): PoczatekUwag {
  const kluczPoId = new Map<string, string>();
  const idSerwera = new Set(serwerowe.map((u) => u.id));
  for (const [klucz, szkic] of szkice) {
    if (szkic.id !== null && idSerwera.has(szkic.id) && !kluczPoId.has(szkic.id)) kluczPoId.set(szkic.id, klucz);
  }

  const uwagi = serwerowe.map((u) => uwagaZSerwera(u, kluczPoId.get(u.id) ?? u.id));
  const otwarte: OtwartaUwaga[] = [];
  const doZapisu: PoczatekUwag["doZapisu"] = [];
  const doUsuniecia: PoczatekUwag["doUsuniecia"] = [];
  const doPorzucenia: string[] = [];
  const ids = new Map<string, string | null>(uwagi.map((u) => [u.klucz, u.id] as const));

  for (const [klucz, szkic] of szkice) {
    const uwaga = uwagi.find((u) => u.klucz === klucz);
    const numer = uwaga ? uwaga.numerWypowiedzi : szkic.numer;
    const numerPoprawny = numer === null || czyNumerWypowiedziWZakresie(numer, liczbaWypowiedzi);
    if (!numerPoprawny) {
      doPorzucenia.push(klucz);
      continue;
    }
    if (szkic.usun) {
      if (uwaga) doUsuniecia.push({ klucz, szkic: { ...szkic, numer, id: uwaga.id } });
      else doPorzucenia.push(klucz);
      continue;
    }
    if (czyPustaTresc(szkic.komentarz)) {
      doPorzucenia.push(klucz);
      continue;
    }
    // Szkic bez uwagi na serwerze to nowa uwaga, nawet jesli w poprzedniej wizycie jej pierwszy zapis zdazyl sie udac.
    const id = uwaga ? uwaga.id : null;
    const wyslij: SzkicUwagi = { ...szkic, numer, id, usun: false };
    ids.set(klucz, id);
    doZapisu.push({ klucz, szkic: wyslij });
    otwarte.push({ klucz, numer, fokus: false, szkic: wyslij });
  }

  return { stan: { uwagi, otwarte, usuwane: [] }, doZapisu, doUsuniecia, doPorzucenia, ids };
}
